import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Avatar, Chip,
  Button, IconButton, TextField, InputAdornment, Tabs, Tab, Paper, Stack,
  CircularProgress, Tooltip, useMediaQuery, useTheme, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Select, InputLabel, FormControl,
  Switch, FormControlLabel,
} from '@mui/material';
import { Add, GroupsRounded, PersonAdd, ExitToApp, Delete, ContentCopy, Check, Close, Search, PersonSearch, PhotoCamera, Send, Settings } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import PostCard from './PostCard';
import { useGroups } from '../hooks/useGroups';
import { authService } from '../services/authService';
import { useNotification } from './NotificationContext';
import {
  groupService, DiveGroup, GroupMembership, GroupJoinMode, GroupJoinModeValue, GroupRole, groupPhotoUrl,
} from '../services/groupService';
import { userProfileService } from '../services/userProfileService';
import MemberPhotoGrid from './MemberPhotoGrid';
import { postService, Post } from '../services/postService';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { scheduledDivesService } from '../services/scheduledDivesService';
import type { ScheduledDive } from '../types/ScheduledDive';
import ScheduleDiveDialog from './ScheduleDiveDialog';
import DiscoverGroupsPanel from './DiscoverGroupsPanel';
import { groupMessageService, GroupMessage } from '../services/groupMessageService';
import { eventService, DiveEvent } from '../services/eventService';
import EventsPanel from './EventsPanel';
import { format } from 'date-fns';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

function GroupAvatar({ name, size = 48, photoUrl }: { name: string; size?: number; photoUrl?: string | null }) {
  return (
    <Avatar src={photoUrl ?? undefined} sx={{ width: size, height: size, bgcolor: 'primary.main', fontWeight: 700, fontSize: size * 0.35 }}>
      {!photoUrl && (initials(name) || '?')}
    </Avatar>
  );
}

// ── Create group dialog ──────────────────────────────────────────────────────

function CreateGroupDialog({
  open, onClose, onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string, description: string, joinMode: GroupJoinModeValue) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [joinMode, setJoinMode] = useState<GroupJoinModeValue>(GroupJoinMode.InviteOnly);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onCreate(name.trim(), description.trim(), joinMode);
      setName('');
      setDescription('');
      setJoinMode(GroupJoinMode.InviteOnly);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Dive Group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Group name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControl fullWidth>
            <InputLabel id="join-mode-label">Who can join</InputLabel>
            <Select
              labelId="join-mode-label"
              label="Who can join"
              value={joinMode}
              onChange={e => setJoinMode(e.target.value as GroupJoinModeValue)}
            >
              <MenuItem value={GroupJoinMode.InviteOnly}>Invite only — admins add members or approve requests</MenuItem>
              <MenuItem value={GroupJoinMode.OpenLink}>Open link — anyone with the code can join instantly</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Group settings dialog ────────────────────────────────────────────────────

function GroupSettingsDialog({
  open, onClose, group, onSave,
}: {
  open: boolean;
  onClose: () => void;
  group: DiveGroup;
  onSave: (searchable: boolean, showOnPublicPage: boolean) => Promise<void>;
}) {
  const [searchable, setSearchable] = useState(group.searchable);
  const [showOnPublicPage, setShowOnPublicPage] = useState(group.showOnPublicPage ?? false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setSearchable(group.searchable); setShowOnPublicPage(group.showOnPublicPage ?? false); }, [group.searchable, group.showOnPublicPage, open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSave(searchable, showOnPublicPage);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Group Settings</DialogTitle>
      <DialogContent>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Switch checked={searchable} onChange={e => setSearchable(e.target.checked)} />}
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Show this group in search</Typography>
              <Typography variant="caption" color="text.secondary">
                When off, this group won't appear in group search results. Members can still find it via an invite or the join link — this only controls discoverability.
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          sx={{ mt: 2 }}
          control={<Switch checked={showOnPublicPage} onChange={e => setShowOnPublicPage(e.target.checked)} />}
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Show on the public home page</Typography>
              <Typography variant="caption" color="text.secondary">
                Lets people who aren't signed in see this group's name, description and member count on the home page. Off by default — nothing else about your group or its members is shown.
              </Typography>
            </Box>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Join by code dialog ──────────────────────────────────────────────────────

function JoinByCodeDialog({
  open, onClose, onJoin,
}: {
  open: boolean;
  onClose: () => void;
  onJoin: (code: string) => Promise<void>;
}) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      await onJoin(code.trim());
      setCode('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Join a Group</DialogTitle>
      <DialogContent>
        <TextField
          label="Group code"
          value={code}
          onChange={e => setCode(e.target.value)}
          fullWidth
          autoFocus
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!code.trim() || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Join
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Groups list (index) ──────────────────────────────────────────────────────

const DiveGroupsList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const { groups, loading, error, createGroup, joinByCode } = useGroups(userId);

  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  const handleCreate = useCallback(async (name: string, description: string, joinMode: GroupJoinModeValue) => {
    try {
      const group = await createGroup(name, description || undefined, joinMode);
      showSuccess('Group created!');
      navigate(`/groups/${group.groupId}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create group');
    }
  }, [createGroup, showSuccess, showError, navigate]);

  const handleJoin = useCallback(async (code: string) => {
    try {
      const group = await joinByCode(code);
      showSuccess(`Joined "${group.name}"`);
      navigate(`/groups/${group.groupId}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to join group');
    }
  }, [joinByCode, showSuccess, showError, navigate]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="join dive groups"
          description="Create or join persistent groups of divers to plan, share, and post together — sign in or create a free account to get started."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 900, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Dive Groups</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setJoinOpen(true)} sx={{ minHeight: 40 }}>
              Join by Code
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)} sx={{ minHeight: 40 }}>
              New Group
            </Button>
          </Stack>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <DiscoverGroupsPanel />
        </Box>

        {!loading && groups.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <GroupsRounded sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No dive groups yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a group of dive buddies, or join one with a code.
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {groups.map(group => (
            <Grid item xs={12} sm={6} md={isMobile ? 12 : 4} key={group.groupId}>
              <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate(`/groups/${group.groupId}`)}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <GroupAvatar name={group.name} photoUrl={group.photoKey ? groupPhotoUrl(group.photoKey) : null} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{group.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {group.memberCount} member{group.memberCount === 1 ? '' : 's'}
                      </Typography>
                    </Box>
                  </Stack>
                  {group.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      {group.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <CreateGroupDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreate} />
        <JoinByCodeDialog open={joinOpen} onClose={() => setJoinOpen(false)} onJoin={handleJoin} />
      </Box>
    </Box>
  );
};

// ── Add member search (mirrors DiveBuddies' FindBuddiesPanel) ──────────────

function AddMemberPanel({
  existingMemberIds,
  onAdd,
  loadingId,
}: {
  existingMemberIds: Set<string>;
  onAdd: (userId: string) => void;
  loadingId: string | null;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserPublicProfile[]>([]);
  const [searching, setSearching] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const found = await buddyService.searchUsers(value);
      setResults(found);
    } finally {
      setSearching(false);
    }
  };

  const filteredResults = results.filter(u => !existingMemberIds.has(u.userId));

  return (
    <Stack spacing={1.5}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search by name or email…"
        value={query}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {searching ? <CircularProgress size={16} /> : <Search fontSize="small" />}
            </InputAdornment>
          ),
        }}
        autoComplete="off"
      />

      {query.length >= 2 && filteredResults.length === 0 && !searching && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <PersonSearch sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary">No users found for "{query}"</Typography>
        </Box>
      )}

      {filteredResults.length > 0 && (
        <Stack spacing={1}>
          {filteredResults.map(u => (
            <Paper key={u.userId} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <GroupAvatar name={u.displayName} size={36} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">{u.diveCountLabel}</Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={loadingId === u.userId ? <CircularProgress size={14} /> : <PersonAdd />}
                  disabled={loadingId === u.userId}
                  onClick={() => onAdd(u.userId)}
                  sx={{ minHeight: 36 }}
                >
                  Add
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

// ── Group chat ───────────────────────────────────────────────────────────────

const CHAT_POLL_INTERVAL_MS = 30_000;

/**
 * A group's chat thread — distinct from the Feed tab (posts) and from 1:1 direct messages
 * (Messages.tsx). One thread per group, so there's no inbox list to show first, unlike DMs.
 * This app has no realtime transport, so new messages arrive via the same 30s polling
 * ceiling every other "live-ish" surface here uses.
 */
function GroupChatPanel({ groupId, currentUserId }: { groupId: string; currentUserId: string }) {
  const { showError } = useNotification();
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      setMessages(await groupMessageService.getMessages(groupId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to load chat');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, CHAT_POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!draft.trim() || sending) return;
    setSending(true);
    try {
      const message = await groupMessageService.sendMessage(groupId, draft.trim());
      setMessages(prev => [...prev, message]);
      setDraft('');
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ display: 'flex', flexDirection: 'column', height: 500, overflow: 'hidden' }}>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {error && <Typography color="error" variant="body2">{error}</Typography>}
        {!loading && !error && messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">No messages yet — say hello to the group.</Typography>
          </Box>
        )}
        {!loading && !error && (
          <Stack spacing={1}>
            {messages.map(m => (
              <Box
                key={m.messageId}
                sx={{
                  alignSelf: m.senderUserId === currentUserId ? 'flex-end' : 'flex-start',
                  maxWidth: '75%',
                }}
              >
                {m.senderUserId !== currentUserId && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 0.5 }}>
                    {m.senderDisplayName}
                  </Typography>
                )}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    bgcolor: m.senderUserId === currentUserId ? 'primary.main' : 'background.default',
                    color: m.senderUserId === currentUserId ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{m.text}</Typography>
                </Paper>
              </Box>
            ))}
            <div ref={bottomRef} />
          </Stack>
        )}
      </Box>

      <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Message the group…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
          autoComplete="off"
        />
        <IconButton color="primary" onClick={handleSend} disabled={!draft.trim() || sending}>
          {sending ? <CircularProgress size={18} /> : <Send fontSize="small" />}
        </IconButton>
      </Stack>
    </Paper>
  );
}

// ── Group detail (members + feed) ───────────────────────────────────────────

const DiveGroupDetail: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;

  const [group, setGroup] = useState<DiveGroup | null>(null);
  const [members, setMembers] = useState<GroupMembership[]>([]);
  const [requests, setRequests] = useState<GroupMembership[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [openDives, setOpenDives] = useState<ScheduledDive[]>([]);
  const [events, setEvents] = useState<DiveEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [postText, setPostText] = useState('');
  const [posting, setPosting] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [joiningDiveId, setJoiningDiveId] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const myMembership = members.find(m => m.userId === userId);
  const canManage = myMembership?.role === GroupRole.Admin || myMembership?.role === GroupRole.Owner;
  // Events(2) is a fixed slot like Open Dives/Members. Requests only exists when canManage,
  // so Chat's index shifts to stay last.
  const chatTabIndex = canManage ? 5 : 4;

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setError(null);
    try {
      const [g, m, f, d, ev] = await Promise.all([
        groupService.getGroup(groupId),
        groupService.getMembers(groupId),
        postService.getGroupFeed(groupId),
        scheduledDivesService.getOpenDivesForGroup(groupId).catch(() => []),
        eventService.getEventsForGroup(groupId).catch(() => []),
      ]);
      setGroup(g);
      setMembers(m);
      setPosts(f);
      setOpenDives(d);
      setEvents(ev);
    } catch {
      setError('Failed to load group');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const handleJoinDive = useCallback(async (diveId: string) => {
    setJoiningDiveId(diveId);
    try {
      const updated = await scheduledDivesService.joinOpenDive(diveId);
      setOpenDives(prev => prev.map(d => (d.diveId === diveId ? updated : d)));
      showSuccess('Joined the dive');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to join dive');
    } finally {
      setJoiningDiveId(null);
    }
  }, [showSuccess, showError]);

  useEffect(() => {
    if (!groupId || !canManage) { setRequests([]); return; }
    groupService.getPendingRequests(groupId).then(setRequests).catch(() => setRequests([]));
  }, [groupId, canManage]);

  const handlePost = useCallback(async () => {
    if (!groupId || !postText.trim()) return;
    setPosting(true);
    try {
      const post = await postService.createGroupPost(groupId, { text: postText.trim() });
      setPosts(prev => [post, ...prev]);
      setPostText('');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to post');
    } finally {
      setPosting(false);
    }
  }, [groupId, postText, showError]);

  const handleDeletePost = useCallback(async (postId: string) => {
    try {
      await postService.deletePost(postId);
      setPosts(prev => prev.filter(p => p.postId !== postId));
      showSuccess('Post deleted');
    } catch {
      showError('Failed to delete post');
    }
  }, [showSuccess, showError]);

  // Pinning a post unpins whatever was previously pinned (server-side), so reflect that
  // locally too: clear isPinned on every other post, then re-sort pinned-first.
  const handlePinChanged = useCallback((updated: Post) => {
    setPosts(prev => {
      const next = prev.map(p => {
        if (p.postId === updated.postId) return updated;
        return updated.isPinned && p.isPinned ? { ...p, isPinned: false } : p;
      });
      return [...next].sort((a, b) => Number(b.isPinned) - Number(a.isPinned));
    });
  }, []);

  const handleInvite = useCallback(async (targetUserId: string) => {
    if (!groupId) return;
    setActionLoadingId(targetUserId);
    try {
      const invited = await groupService.inviteMember(groupId, targetUserId);
      showSuccess(`${invited.displayName} added`);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setActionLoadingId(null);
    }
  }, [groupId, showSuccess, showError, load]);

  const handleApprove = useCallback(async (targetUserId: string) => {
    if (!groupId) return;
    setActionLoadingId(targetUserId);
    try {
      await groupService.approveRequest(groupId, targetUserId);
      showSuccess('Request approved');
      setRequests(prev => prev.filter(r => r.userId !== targetUserId));
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setActionLoadingId(null);
    }
  }, [groupId, showSuccess, showError, load]);

  const handleDecline = useCallback(async (targetUserId: string) => {
    if (!groupId) return;
    setActionLoadingId(targetUserId);
    try {
      await groupService.declineOrCancelRequest(groupId, targetUserId);
      setRequests(prev => prev.filter(r => r.userId !== targetUserId));
    } catch {
      showError('Failed to decline request');
    } finally {
      setActionLoadingId(null);
    }
  }, [groupId, showError]);

  const handleRemoveMember = useCallback(async (targetUserId: string) => {
    if (!groupId) return;
    setActionLoadingId(targetUserId);
    try {
      await groupService.removeMember(groupId, targetUserId);
      setMembers(prev => prev.filter(m => m.userId !== targetUserId));
      showSuccess('Member removed');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setActionLoadingId(null);
    }
  }, [groupId, showSuccess, showError]);

  const handleLeave = useCallback(async () => {
    if (!groupId || !userId) return;
    try {
      await groupService.removeMember(groupId, userId);
      showSuccess('Left group');
      navigate('/groups');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to leave group');
    }
  }, [groupId, userId, showSuccess, showError, navigate]);

  const handleDeleteGroup = useCallback(async () => {
    if (!groupId) return;
    try {
      await groupService.deleteGroup(groupId);
      showSuccess('Group deleted');
      navigate('/groups');
    } catch {
      showError('Failed to delete group');
    }
  }, [groupId, showSuccess, showError, navigate]);

  const handleCopyCode = useCallback(() => {
    if (!group) return;
    navigator.clipboard?.writeText(group.shareCode).then(() => showSuccess('Code copied'));
  }, [group, showSuccess]);

  const handleSaveSettings = useCallback(async (searchable: boolean, showOnPublicPage: boolean) => {
    if (!groupId) return;
    try {
      const updated = await groupService.updateGroup(groupId, { searchable, showOnPublicPage });
      setGroup(updated);
      showSuccess('Group settings updated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update group settings');
      throw err;
    }
  }, [groupId, showSuccess, showError]);

  // Reuses the same presigned-upload flow profile photos use — same public bucket, same
  // allowlist — there's no group-specific upload endpoint, nor does one need to exist.
  const handlePhotoSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !groupId) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      showError('Please choose a JPEG, PNG, or WEBP image');
      return;
    }
    setUploadingPhoto(true);
    try {
      const photoKey = await userProfileService.uploadPhoto(file);
      const updated = await groupService.updateGroup(groupId, { photoKey });
      setGroup(updated);
      showSuccess('Group photo updated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update group photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  }, [groupId, showSuccess, showError]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="view this dive group"
          description="Sign in or create a free account to view this group."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <AppNavigation />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  if (error || !group) {
    return (
      <Box>
        <AppNavigation />
        <Box sx={{ px: 3, py: 4 }}>
          <Typography color="error">{error ?? "You don't have access to this group"}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ position: 'relative' }}>
              <GroupAvatar name={group.name} size={64} photoUrl={group.photoKey ? groupPhotoUrl(group.photoKey) : null} />
              {canManage && (
                <Tooltip title="Change group photo">
                  <IconButton
                    size="small"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    sx={{
                      position: 'absolute', bottom: -4, right: -4, bgcolor: 'background.paper',
                      border: '1px solid', borderColor: 'divider', width: 24, height: 24,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {uploadingPhoto ? <CircularProgress size={12} /> : <PhotoCamera sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Tooltip>
              )}
              <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handlePhotoSelected} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{group.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {group.memberCount} member{group.memberCount === 1 ? '' : 's'} · {group.joinMode === GroupJoinMode.OpenLink ? 'Open link' : 'Invite only'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            {canManage && (
              <Tooltip title="Group settings">
                <IconButton onClick={() => setSettingsOpen(true)}><Settings /></IconButton>
              </Tooltip>
            )}
            {myMembership?.role === GroupRole.Owner ? (
              <Button color="error" startIcon={<Delete />} onClick={handleDeleteGroup}>Delete Group</Button>
            ) : (
              <Button color="error" startIcon={<ExitToApp />} onClick={handleLeave}>Leave Group</Button>
            )}
          </Stack>
        </Box>

        {group.description && (
          <Typography variant="body1" sx={{ mb: 1.5 }}>{group.description}</Typography>
        )}

        <MemberPhotoGrid
          members={members.map(m => ({ id: m.userId, displayName: m.displayName, profilePhotoUrl: m.profilePhotoUrl }))}
        />
        <Box sx={{ mb: 2 }} />

        {group.joinMode === GroupJoinMode.OpenLink && canManage && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2">Join code: <strong>{group.shareCode}</strong></Typography>
            <Button size="small" startIcon={<ContentCopy />} onClick={handleCopyCode}>Copy</Button>
          </Paper>
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Feed" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          <Tab label={`Open Dives (${openDives.length})`} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          <Tab label={`Events (${events.length})`} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          <Tab label={`Members (${members.length})`} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          {canManage && (
            <Tab label={`Requests${requests.length ? ` (${requests.length})` : ''}`} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          )}
          <Tab label="Chat" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
        </Tabs>

        {tab === 0 && (
          <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack spacing={1.5}>
                <TextField
                  placeholder={`Share something with ${group.name}…`}
                  value={postText}
                  onChange={e => setPostText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handlePost}
                    disabled={!postText.trim() || posting}
                    startIcon={posting ? <CircularProgress size={16} /> : undefined}
                  >
                    Post
                  </Button>
                </Box>
              </Stack>
            </Paper>

            {posts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">No posts yet — be the first to share something.</Typography>
              </Box>
            )}

            {posts.map(post => (
              <PostCard
                key={post.postId}
                post={post}
                currentUserId={userId}
                onDelete={handleDeletePost}
                canManageGroupOrClub={canManage}
                onPinChanged={handlePinChanged}
              />
            ))}
          </Box>
        )}

        {tab === 1 && (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setScheduleDialogOpen(true)}>
                Schedule an Open Dive
              </Button>
            </Box>

            {openDives.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No open dives yet — schedule one for the group to join.
                </Typography>
              </Box>
            )}

            {openDives.map(dive => {
              const alreadyJoined = dive.ownerUserId === userId
                || dive.invitees.some(i => i.userId === userId && i.status === 'accepted');
              return (
                <Paper key={dive.diveId} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {dive.siteName || 'Dive'} · {format(new Date(dive.scheduledDate), 'MMM d, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Organized by {dive.ownerDisplayName}
                        {dive.charterCompany ? ` · ${dive.charterCompany}` : ''}
                      </Typography>
                    </Box>
                    {alreadyJoined ? (
                      <Chip label={dive.ownerUserId === userId ? 'You organized this' : "You're in"} size="small" color="primary" />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleJoinDive(dive.diveId)}
                        disabled={joiningDiveId === dive.diveId}
                        startIcon={joiningDiveId === dive.diveId ? <CircularProgress size={14} /> : undefined}
                      >
                        Join
                      </Button>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}

        {tab === 2 && groupId && (
          <EventsPanel groupId={groupId} currentUserId={userId} />
        )}

        {tab === 3 && (
          <Stack spacing={2}>
            {canManage && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Add a member</Typography>
                <AddMemberPanel
                  existingMemberIds={new Set(members.map(m => m.userId))}
                  onAdd={handleInvite}
                  loadingId={actionLoadingId}
                />
              </Paper>
            )}

            {members.map(member => (
              <Paper key={member.userId} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <GroupAvatar name={member.displayName} size={40} photoUrl={member.profilePhotoUrl} />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{member.displayName}</Typography>
                  </Box>
                  <Chip
                    label={member.role}
                    size="small"
                    color={member.role === GroupRole.Owner ? 'primary' : member.role === GroupRole.Admin ? 'secondary' : 'default'}
                  />
                  {canManage && member.role !== GroupRole.Owner && member.userId !== userId && (
                    <Tooltip title="Remove member">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveMember(member.userId)}
                        disabled={actionLoadingId === member.userId}
                        sx={{ minHeight: 40, minWidth: 40 }}
                      >
                        {actionLoadingId === member.userId ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {tab === 4 && canManage && (
          <Stack spacing={1.5}>
            {requests.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">No pending requests</Typography>
              </Box>
            )}
            {requests.map(req => (
              <Paper key={req.userId} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <GroupAvatar name={req.displayName} size={40} />
                  <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>{req.displayName}</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={actionLoadingId === req.userId ? <CircularProgress size={14} /> : <Check />}
                      disabled={actionLoadingId === req.userId}
                      onClick={() => handleApprove(req.userId)}
                      sx={{ minHeight: 36 }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Close />}
                      disabled={actionLoadingId === req.userId}
                      onClick={() => handleDecline(req.userId)}
                      sx={{ minHeight: 36 }}
                    >
                      Decline
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {tab === chatTabIndex && groupId && userId && (
          <GroupChatPanel groupId={groupId} currentUserId={userId} />
        )}
      </Box>

      {groupId && (
        <ScheduleDiveDialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          onSaved={(dive) => {
            setOpenDives(prev => [...prev, dive].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)));
          }}
          initialOpenTo={{ groupId }}
        />
      )}

      {group && (
        <GroupSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          group={group}
          onSave={handleSaveSettings}
        />
      )}
    </Box>
  );
};

export { DiveGroupsList, DiveGroupDetail };
